import React from 'react';
import { PhoneCallIcon } from '@phosphor-icons/react';
import { toast } from 'react-toastify';
import { withBlockExtensions } from '@plone/volto/helpers';
import { MPFButton } from 'volto-ministerio-publico-federal/components/UI';
import { useMediaQuery } from 'volto-ministerio-publico-federal/hooks/useMediaQuery';
import { copyToClipboard } from 'volto-ministerio-publico-federal/hooks/useSharePage';

import {
  CONTATO_DESTAQUE_BOTAO_ROTULO,
  CONTATO_DESTAQUE_COPIADO,
  CONTATO_DESTAQUE_ERRO_COPIA,
  CONTATO_DESTAQUE_TELEFONE_ROTULO,
  CONTATO_DESTAQUE_TITULO,
  MIDIA_COPIA_NUMERO,
  nomeDoBotao,
  toDiscagemHref,
  type ContatoDestaqueData,
} from './helpers';

interface ViewProps {
  data: ContatoDestaqueData;
  isEditMode?: boolean;
}

const ROOT_CLASS = 'mpf-contato-destaque not-prose';

const TOAST_ID = 'mpf-contato-destaque-copia';

export const MpfContatoDestaqueBlockView: React.FC<ViewProps> = ({
  data,
  isEditMode,
}) => {
  const tituloId = React.useId();
  const telefone = data?.telefone?.trim();
  const href = toDiscagemHref(telefone);
  const copia = useMediaQuery(MIDIA_COPIA_NUMERO);

  if (!telefone || !href) {
    return isEditMode ? (
      <section className={`${ROOT_CLASS} mpf-contato-destaque--vazio`}>
        <p className="mpf-contato-destaque__aviso">
          Informe o telefone na barra lateral. Sem ele, o bloco não aparece na
          página.
        </p>
      </section>
    ) : null;
  }

  const titulo = data?.titulo?.trim() || CONTATO_DESTAQUE_TITULO;
  const telefoneRotulo =
    data?.telefoneRotulo?.trim() || CONTATO_DESTAQUE_TELEFONE_ROTULO;
  const botaoRotulo =
    data?.botaoRotulo?.trim() || CONTATO_DESTAQUE_BOTAO_ROTULO;
  const texto = data?.texto?.trim();

  const copiarNumero = (event: React.MouseEvent) => {
    if (isEditMode || !copia) return;
    event.preventDefault();
    copyToClipboard(telefone)
      .then(() =>
        toast.success(CONTATO_DESTAQUE_COPIADO, { toastId: TOAST_ID }),
      )
      .catch(() =>
        toast.error(CONTATO_DESTAQUE_ERRO_COPIA, { toastId: TOAST_ID }),
      );
  };

  return (
    <section className={ROOT_CLASS} aria-labelledby={tituloId}>
      <span className="mpf-contato-destaque__icone" aria-hidden="true">
        <PhoneCallIcon />
      </span>

      <div className="mpf-contato-destaque__textos">
        <h2 id={tituloId} className="mpf-contato-destaque__titulo">
          {titulo}
        </h2>
        {texto && <p className="mpf-contato-destaque__texto">{texto}</p>}
      </div>

      <div className="mpf-contato-destaque__numero">
        <p className="mpf-contato-destaque__telefone">{telefone}</p>
        <p className="mpf-contato-destaque__telefone-rotulo">
          {telefoneRotulo}
        </p>

        <MPFButton
          as={isEditMode ? 'button' : 'a'}
          href={isEditMode ? undefined : href}
          size="m"
          className="mpf-contato-destaque__botao"
          onClick={copiarNumero}
          aria-label={nomeDoBotao(
            botaoRotulo,
            telefone,
            telefoneRotulo,
            copia && !isEditMode,
          )}
        >
          <PhoneCallIcon aria-hidden="true" />
          {botaoRotulo}
        </MPFButton>
      </div>
    </section>
  );
};

export default withBlockExtensions(MpfContatoDestaqueBlockView);
